/**
 * Service provider contracts — what each provider has agreed to do on the
 * airport, to what standard, and until when. Nothing financial.
 *
 * The register is read by the people who manage the providers day to day:
 * operations, the duty managers, the contract owners. They need the SCOPE (what
 * the provider does and does not do), the SERVICE LEVELS (what "done" means,
 * measured how), and the TERM (when it ends, and — the date that is missed —
 * by when notice must be given). They do not need, and must not be handed, the
 * prices: a register open to operations that carried rates would put
 * commercial terms in front of everybody who can see a stand. So the register
 * refuses a financial figure outright (`financialTerms`), and the signed copy,
 * which does carry prices, is filed in the document library as a RESTRICTED
 * document whose distribution the library controls.
 *
 * Nothing is overwritten. Every save is a new version of the contract; the
 * current terms are the latest version, and the history shows who changed
 * which term, from what, to what — which is the question an auditor asks of a
 * service level that quietly got easier.
 */

/* ── what a contract is ───────────────────────────────────────────────── */

export type ProviderKind =
  | "ground_handling"
  | "fuel"
  | "catering"
  | "cleaning"
  | "security"
  | "maintenance"
  | "it_systems"
  | "construction"
  | "airline"
  | "government"
  | "other";

export const PROVIDER_KINDS: { key: ProviderKind; label: string }[] = [
  { key: "ground_handling", label: "Ground handling" },
  { key: "fuel", label: "Fuel" },
  { key: "catering", label: "Catering" },
  { key: "cleaning", label: "Cleaning" },
  { key: "security", label: "Security" },
  { key: "maintenance", label: "Maintenance" },
  { key: "it_systems", label: "IT and systems" },
  { key: "construction", label: "Construction and works" },
  { key: "airline", label: "Airline" },
  { key: "government", label: "Government agency" },
  { key: "other", label: "Other" },
];
export const KIND_LABEL = Object.fromEntries(PROVIDER_KINDS.map((k) => [k.key, k.label])) as Record<
  ProviderKind,
  string
>;

export type Renewal = "fixed" | "auto" | "option";
export const RENEWAL_LABEL: Record<Renewal, string> = {
  fixed: "Ends on its expiry date",
  auto: "Renews automatically unless notice is given",
  option: "Can be extended by option",
};

export type Frequency = "per_event" | "daily" | "weekly" | "monthly" | "quarterly" | "annually";
export const FREQUENCY_LABEL: Record<Frequency, string> = {
  per_event: "Each occurrence",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

export interface ServiceLevel {
  id: string;
  /** what is measured, e.g. "First bag on the belt" */
  measure: string;
  /** the standard, e.g. "within 15 minutes of on-blocks for 95% of flights" */
  target: string;
  /** how often it is reported */
  frequency: Frequency;
  /** who measures it, e.g. "Airport operations from the BRS log" */
  measuredBy?: string;
  /** what happens on a breach, in words — never an amount */
  onBreach?: string;
}

export interface ContractDocument {
  fileName: string;
  size: number;
  type: string;
  /** the library document and revision holding the signed copy, where the platform took it */
  documentId?: string;
  revisionId?: string;
  /** the key of a copy kept in this browser */
  localKey?: string;
}

export interface Contract {
  id: string;
  provider: string;
  kind: ProviderKind;
  title: string;
  reference?: string;
  /** what the provider does, in the contract's own words */
  scope: string;
  /** the services, one per line */
  services: string[];
  /** what the provider does NOT do — the half of a scope that settles arguments */
  exclusions?: string;
  serviceLevels: ServiceLevel[];
  start?: string;
  end?: string;
  noticeDays?: number;
  renewal: Renewal;
  /** the airport's contract owner */
  owner?: string;
  contact?: { name?: string; phone?: string; email?: string };
  /** the provider's insurance certificate, which lapses on its own */
  insuranceExpiry?: string;
  notes?: string;
  document?: ContractDocument;
}

export interface ContractVersion {
  id: string;
  contractId: string;
  at: string;
  by: string;
  reason?: string;
  data: Contract;
}

export interface ContractRegister {
  versions: ContractVersion[];
}

/* ── no financials ────────────────────────────────────────────────────── */

const MONEY = [
  /\b(SAR|SR|USD|EUR|GBP|AED|QAR|KWD|BHD|OMR)\b/i,
  /[$€£¥]/,
  /\b(riyals?|dollars?|euros?|pounds sterling|dirhams?)\b/i,
  /ريال|ر\.س|دولار|يورو|درهم/,
  /\b(price|pricing|rates? card|unit rate|fee of|fees of|charge of|invoice|payment terms?|contract value|lump sum|retention of \d|liquidated damages of)\b/i,
];

/**
 * The text of a contract that looks financial, field by field. Any hit blocks
 * the save: the register holds no financial terms, and a figure typed into a
 * scope is still a figure in front of everybody who can read the scope.
 */
export function financialTerms(c: Contract): { field: string; text: string }[] {
  const hits: { field: string; text: string }[] = [];
  const look = (field: string, text: string | undefined) => {
    if (!text) return;
    for (const re of MONEY) {
      const m = re.exec(text);
      if (m) {
        const at = Math.max(0, m.index - 20);
        hits.push({ field, text: text.slice(at, m.index + m[0].length + 20).trim() });
        return;
      }
    }
  };
  look("title", c.title);
  look("scope", c.scope);
  c.services.forEach((s, i) => look(`service ${i + 1}`, s));
  look("exclusions", c.exclusions);
  c.serviceLevels.forEach((sl, i) => {
    look(`service level ${i + 1}`, sl.measure);
    look(`service level ${i + 1}`, sl.target);
    look(`service level ${i + 1}`, sl.onBreach);
  });
  look("notes", c.notes);
  return hits;
}

/* ── checking a contract ──────────────────────────────────────────────── */

export const isDate = (s: string | undefined) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s));

export interface Problem {
  field: string;
  /** English, and an Arabic key; `vars` fill its placeholders */
  message: string;
  vars?: Record<string, string>;
}

export function contractProblems(c: Contract): Problem[] {
  const out: Problem[] = [];
  if (!c.provider.trim()) out.push({ field: "provider", message: "Name the provider." });
  if (!c.title.trim()) out.push({ field: "title", message: "Give the contract a title." });
  if (!c.scope.trim()) out.push({ field: "scope", message: "Describe the scope — what the provider does." });
  for (const [f, v] of [
    ["start", c.start],
    ["end", c.end],
    ["insuranceExpiry", c.insuranceExpiry],
  ] as const)
    if (v && !isDate(v)) out.push({ field: f, message: "A date is written YYYY-MM-DD." });
  if (isDate(c.start) && isDate(c.end) && c.end! < c.start!)
    out.push({ field: "end", message: "The contract ends before it starts." });
  if (c.noticeDays != null && (!Number.isInteger(c.noticeDays) || c.noticeDays < 0 || c.noticeDays > 730))
    out.push({ field: "noticeDays", message: "The notice period is a number of days, 0 to 730." });
  c.serviceLevels.forEach((sl, i) => {
    if (!sl.measure.trim() || !sl.target.trim())
      out.push({ field: `sl${i}`, message: "Each service level needs what is measured and the standard." });
  });
  for (const h of financialTerms(c))
    out.push({
      field: "financial",
      message: "This register holds no financial terms. Remove the figure: “{text}”.",
      vars: { text: h.text },
    });
  return out;
}

/* ── the register ─────────────────────────────────────────────────────── */

export interface Who {
  by: string;
  at: string;
  newId: () => string;
}

/** Save a contract as a new version; the earlier versions stay. */
export function saveContract(reg: ContractRegister, c: Contract, who: Who, reason?: string): ContractRegister {
  const problems = contractProblems(c);
  if (problems.length) throw new Error(problems.map((p) => p.message).join(" "));
  const prev = current(reg).find((x) => x.data.id === c.id);
  if (prev && JSON.stringify(prev.data) === JSON.stringify(c)) return reg;
  return {
    versions: [
      ...reg.versions,
      { id: who.newId(), contractId: c.id, at: who.at, by: who.by, reason: reason?.trim() || undefined, data: c },
    ],
  };
}

/** The latest version of each contract. */
export function current(reg: ContractRegister): ContractVersion[] {
  const last = new Map<string, ContractVersion>();
  for (const v of [...reg.versions].sort((a, b) => a.at.localeCompare(b.at))) last.set(v.contractId, v);
  return [...last.values()];
}

export function historyOf(reg: ContractRegister, contractId: string): ContractVersion[] {
  return reg.versions.filter((v) => v.contractId === contractId).sort((a, b) => b.at.localeCompare(a.at));
}

/** Every copy of the register, merged by version id — nothing is ever edited, so a union loses nothing. */
export function mergeRegisters(copies: readonly ContractRegister[]): ContractRegister {
  const by = new Map<string, ContractVersion>();
  for (const r of copies) for (const v of r?.versions ?? []) if (!by.has(v.id)) by.set(v.id, v);
  return { versions: [...by.values()].sort((a, b) => a.at.localeCompare(b.at)) };
}

/** Which terms moved between two versions, by label. */
export function changedTerms(before: Contract | undefined, after: Contract): string[] {
  if (!before) return ["Added"];
  const out: string[] = [];
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const pairs: [string, unknown, unknown][] = [
    ["Provider", before.provider, after.provider],
    ["Type of provider", before.kind, after.kind],
    ["Title", before.title, after.title],
    ["Reference", before.reference, after.reference],
    ["Scope", before.scope, after.scope],
    ["Services", before.services, after.services],
    ["Exclusions", before.exclusions, after.exclusions],
    ["Service levels", before.serviceLevels, after.serviceLevels],
    ["Start", before.start, after.start],
    ["Expiry", before.end, after.end],
    ["Notice period", before.noticeDays, after.noticeDays],
    ["Renewal", before.renewal, after.renewal],
    ["Contract owner", before.owner, after.owner],
    ["Provider contact", before.contact, after.contact],
    ["Insurance expiry", before.insuranceExpiry, after.insuranceExpiry],
    ["Notes", before.notes, after.notes],
    ["Signed copy", before.document?.fileName, after.document?.fileName],
  ];
  for (const [label, a, b] of pairs) if (!same(a, b)) out.push(label);
  return out;
}

/* ── where each contract stands ───────────────────────────────────────── */

export type Standing = "not_started" | "active" | "notice_due" | "expiring" | "expired" | "no_expiry";

export const STANDING_LABEL: Record<Standing, string> = {
  not_started: "Not started",
  active: "In force",
  notice_due: "Notice decision due",
  expiring: "Expiring",
  expired: "Expired",
  no_expiry: "No expiry recorded",
};

/** How long before the notice deadline the register starts saying so. */
export const NOTICE_WARNING_DAYS = 45;
/** How long before expiry a contract counts as expiring. */
export const EXPIRY_WARNING_DAYS = 90;

export interface Flag {
  kind: "bad" | "warn";
  what: string;
}

export interface ContractRow {
  version: ContractVersion;
  c: Contract;
  standing: Standing;
  daysToEnd: number | null;
  /** the last day notice can be given, where there is a notice period */
  noticeBy: string | null;
  daysToNotice: number | null;
  flags: Flag[];
}

const DAY = 86_400_000;
const daysFrom = (now: Date, iso: string) => Math.floor((Date.parse(`${iso}T00:00:00Z`) - now.getTime()) / DAY);

export function rowFor(v: ContractVersion, now: Date): ContractRow {
  const c = v.data;
  const daysToEnd = isDate(c.end) ? daysFrom(now, c.end!) : null;
  let noticeBy: string | null = null;
  if (isDate(c.end) && c.noticeDays != null) {
    const d = new Date(`${c.end}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - c.noticeDays);
    noticeBy = d.toISOString().slice(0, 10);
  }
  const daysToNotice = noticeBy ? daysFrom(now, noticeBy) : null;
  let standing: Standing;
  if (isDate(c.start) && daysFrom(now, c.start!) > 0) standing = "not_started";
  else if (daysToEnd == null) standing = "no_expiry";
  else if (daysToEnd < 0) standing = "expired";
  else if (daysToNotice != null && daysToNotice <= NOTICE_WARNING_DAYS && c.renewal !== "fixed")
    standing = "notice_due";
  else if (daysToEnd <= EXPIRY_WARNING_DAYS) standing = "expiring";
  else standing = "active";

  const flags: Flag[] = [];
  if (standing === "expired") flags.push({ kind: "bad", what: "The contract has expired" });
  if (standing === "no_expiry") flags.push({ kind: "warn", what: "No expiry date recorded" });
  if (daysToNotice != null && daysToNotice < 0 && (daysToEnd ?? 0) >= 0 && c.renewal === "auto")
    flags.push({ kind: "warn", what: "The notice deadline has passed: it renews" });
  if (isDate(c.insuranceExpiry) && daysFrom(now, c.insuranceExpiry!) < 0)
    flags.push({ kind: "bad", what: "The provider's insurance has expired" });
  if (!c.insuranceExpiry) flags.push({ kind: "warn", what: "No insurance expiry recorded" });
  if (c.serviceLevels.length === 0) flags.push({ kind: "warn", what: "No service levels recorded" });
  if (!c.document) flags.push({ kind: "warn", what: "No signed copy attached" });
  if (!c.owner) flags.push({ kind: "warn", what: "No contract owner named" });
  return { version: v, c, standing, daysToEnd, noticeBy, daysToNotice, flags };
}

export const STANDING_ORDER: Standing[] = ["expired", "notice_due", "expiring", "no_expiry", "not_started", "active"];

export function rowsOf(reg: ContractRegister, now: Date): ContractRow[] {
  return current(reg)
    .map((v) => rowFor(v, now))
    .sort(
      (a, b) =>
        STANDING_ORDER.indexOf(a.standing) - STANDING_ORDER.indexOf(b.standing) ||
        (a.daysToEnd ?? 1e9) - (b.daysToEnd ?? 1e9) ||
        a.c.provider.localeCompare(b.c.provider),
    );
}

export function summary(rows: readonly ContractRow[]) {
  return {
    total: rows.length,
    providers: new Set(rows.map((r) => r.c.provider.trim().toLowerCase())).size,
    expired: rows.filter((r) => r.standing === "expired").length,
    noticeDue: rows.filter((r) => r.standing === "notice_due").length,
    expiring: rows.filter((r) => r.standing === "expiring").length,
    noDocument: rows.filter((r) => !r.c.document).length,
    serviceLevels: rows.reduce((n, r) => n + r.c.serviceLevels.length, 0),
  };
}
